/**
 * Footnotes Modal Component
 *
 * Modal for managing footnotes with local state until save:
 * - Add: Creates new footnote and links to current deckline
 * - Update: Modifies footnote description
 * - Link/Unlink: Toggles footnote association with deckline
 * - Delete: Removes footnote immediately with confirmation, reindexes remaining, updates all decklines
 *
 * Changes to descriptions and links are kept in local state until "Save" is clicked.
 * Deletions are immediate after confirmation.
 * Cancel reverts all non-delete operations.
 *
 * @returns Footnotes modal dialog component
 */

import './FootnotesModal.scss';

import { useDecklinesContext } from '@context/DecklinesContext';
import { useFootnotesContext } from '@context/FootnotesContext';
import ConfirmDialog from '@deckManagement/components/Common/Dialogs/ConfirmDialog';
import FootnotesTable from '@deckManagement/components/Page/Footnotes/FootnotesTable';
import { Add as AddIcon } from '@mui/icons-material';
import { Alert, Box, Button, Dialog, DialogActions, DialogContent, DialogTitle, Typography } from '@mui/material';
import { createId } from '@paralleldrive/cuid2';
import { isEqual } from 'lodash';
import { useCallback, useEffect, useState } from 'react';
import { FootnotesModalProps, FootnoteTableRow } from 'types/Footnotes.types';
import { EnhancedFootnote } from 'types/FootnotesContext.types';

const FootnotesModal = ({ isEditingDisabled }: FootnotesModalProps) => {
  const { selectedDeckline, updatePageDeckline, pageDecklines } = useDecklinesContext();
  const {
    footnotes: contextFootnotes,
    displayFootnotesModal,
    setDisplayFootnotesModal,
    updateFootnotes,
  } = useFootnotesContext();

  // Local state for footnotes (changes not committed until save)
  const [localFootnotes, setLocalFootnotes] = useState<EnhancedFootnote[]>([]);
  const [localDecklineFootnotes, setLocalDecklineFootnotes] = useState<number[]>([]);
  const [validationError, setValidationError] = useState<string>('');
  const [deleteConfirmation, setDeleteConfirmation] = useState<{ open: boolean; footnoteId: string }>({
    open: false,
    footnoteId: '',
  });

  useEffect(() => {
    if (displayFootnotesModal && selectedDeckline) {
      setLocalFootnotes([...contextFootnotes]);

      if (
        Array.isArray(selectedDeckline.footnotes) &&
        selectedDeckline.footnotes.every((fn) => typeof fn === 'number')
      ) {
        setLocalDecklineFootnotes(selectedDeckline.footnotes);
      }

      setValidationError('');
    }
  }, [displayFootnotesModal, selectedDeckline, contextFootnotes]);

  // Convert local footnotes to table format, excluding deleted footnotes
  const tableData: FootnoteTableRow[] = localFootnotes
    .filter((footnote) => !footnote.metadata.isDeleted)
    .map((footnote) => ({
      id: footnote.metadata.internalId,
      footnoteNo: footnote.footnote_no || 'None',
      description: footnote.footnote_description || '',
      linked: localDecklineFootnotes.includes(parseInt(footnote.footnote_no || '0', 10)),
      isNew: footnote.metadata.isNewRow || false,
    }));

  /**
   * Add new footnote and link to current deckline
   * Calculates next available footnote number and creates new footnote with empty description
   */
  const handleAddFootnote = useCallback(() => {
    if (!selectedDeckline) return;

    // Calculate next footnote number
    const maxNum = Math.max(0, ...localFootnotes.filter((f) => f.footnote_no).map((f) => parseInt(f.footnote_no!, 10)));
    const newFootnoteNum = maxNum + 1;
    const footnoteId = createId();

    // Create new footnote
    const newFootnote: EnhancedFootnote = {
      __typename: 'Footnote',
      pk: selectedDeckline.pk!,
      sk: `FOOTNOTE#${footnoteId}`,
      footnote_no: newFootnoteNum.toString(),
      footnote_description: '',
      metadata: {
        internalId: footnoteId,
        isNewRow: true,
        isModified: false,
        isDeleted: false,
      },
    };

    setLocalFootnotes((prev) => [...prev, newFootnote]);
    setLocalDecklineFootnotes((prev) => [...prev, newFootnoteNum]);
  }, [selectedDeckline, localFootnotes]);

  /**
   * Update footnote description or link status
   * Changes are kept in local state until save
   *
   * @param rowId - Internal footnote ID (internalId from metadata)
   * @param updates - Partial updates to apply (description and/or linked status)
   */
  const handleUpdateRow = useCallback(
    (rowId: string, updates: Partial<FootnoteTableRow>) => {
      // Update description
      if (updates.description !== undefined) {
        setLocalFootnotes((prev) =>
          prev.map((f) =>
            f.metadata.internalId === rowId
              ? {
                  ...f,
                  footnote_description: updates.description,
                  metadata: { ...f.metadata, isModified: !f.metadata.isNewRow },
                }
              : f,
          ),
        );
      }

      // Update link status
      if (updates.linked !== undefined) {
        const footnote = localFootnotes.find((f) => f.metadata.internalId === rowId);
        if (footnote) {
          const footnoteNum = parseInt(footnote.footnote_no || '0', 10);
          if (updates.linked) {
            // Link footnote and maintain sorted order
            setLocalDecklineFootnotes((prev) =>
              prev.includes(footnoteNum) ? prev : [...prev, footnoteNum].sort((a, b) => a - b),
            );
          } else {
            // Unlink footnote
            setLocalDecklineFootnotes((prev) => prev.filter((fn) => fn !== footnoteNum));
          }
        }
      }
    },
    [localFootnotes],
  );

  /**
   * Show delete confirmation dialog
   * @param rowId - Internal footnote ID to delete
   */
  const handleDeleteRow = useCallback((rowId: string) => {
    setDeleteConfirmation({ open: true, footnoteId: rowId });
  }, []);

  /**
   * Delete footnote immediately after confirmation
   * For new footnotes: removes from list entirely
   * For existing footnotes: marks as deleted
   * Reindexes remaining footnotes and updates all affected decklines
   */
  const confirmDelete = useCallback(() => {
    const internalId = deleteConfirmation.footnoteId;
    const footnoteToDelete = localFootnotes.find((f) => f.metadata.internalId === internalId);

    if (!footnoteToDelete) {
      setDeleteConfirmation({ open: false, footnoteId: '' });
      return;
    }

    const deletedNum = parseInt(footnoteToDelete.footnote_no || '0', 10);
    const isNewFootnote = footnoteToDelete.metadata.isNewRow;

    const updatedFootnotes = localFootnotes
      // Remove newly created footnotes entirely, keep existing ones for soft delete
      .filter((f) => !(isNewFootnote && f.metadata.internalId === internalId))
      .map((f) => {
        // Soft delete existing footnotes by marking isDeleted
        if (!isNewFootnote && f.metadata.internalId === internalId) {
          return { ...f, metadata: { ...f.metadata, isDeleted: true } };
        }

        // Skip already deleted footnotes during reindexing
        if (f.metadata.isDeleted) {
          return f;
        }

        // Reindex footnotes with numbers greater than the deleted one
        const num = parseInt(f.footnote_no || '0', 10);
        if (num > deletedNum) {
          return {
            ...f,
            footnote_no: (num - 1).toString(),
            // Keep isNewRow status for newly created footnotes during reindexing
            // Only mark as isModified if it's an existing footnote
            metadata: { ...f.metadata, isModified: !f.metadata.isNewRow },
          };
        }

        return f;
      });

    setLocalFootnotes(updatedFootnotes);
    updateFootnotes(updatedFootnotes);

    const updatedDecklineFootnotes = localDecklineFootnotes
      .filter((fn) => fn !== deletedNum)
      .map((fn) => (fn > deletedNum ? fn - 1 : fn));

    setLocalDecklineFootnotes(updatedDecklineFootnotes);

    if (selectedDeckline) {
      updatePageDeckline(
        selectedDeckline.metadata.internalId,
        'footnotes',
        updatedDecklineFootnotes.length > 0 ? updatedDecklineFootnotes : [],
      );
    }

    pageDecklines.forEach((deckline) => {
      if (deckline.metadata.internalId === selectedDeckline?.metadata.internalId) return;
      if (!deckline.footnotes || deckline.footnotes.length === 0) return;

      const updated = deckline.footnotes
        .filter((fn) => fn && fn !== deletedNum)
        .map((fn) => (fn && fn > deletedNum ? fn - 1 : fn));

      if (!isEqual(updated, deckline.footnotes)) {
        updatePageDeckline(deckline.metadata.internalId, 'footnotes', updated.length > 0 ? updated : []);
      }
    });

    setDeleteConfirmation({ open: false, footnoteId: '' });
  }, [
    deleteConfirmation.footnoteId,
    localFootnotes,
    localDecklineFootnotes,
    selectedDeckline,
    pageDecklines,
    updateFootnotes,
    updatePageDeckline,
  ]);

  /**
   * Save all changes to context and update decklines
   * Validates that no footnotes have empty descriptions before saving
   * Commits local changes to FootnotesContext and DecklinesContext
   */
  const handleSave = useCallback(() => {
    if (!selectedDeckline) return;

    const hasEmptyFootnote = localFootnotes.some(
      (f) => !f.metadata.isDeleted && (!f.footnote_description || f.footnote_description.trim() === ''),
    );

    if (hasEmptyFootnote) {
      setValidationError('An empty footnote cannot be added. Edit the footnote or delete it to continue.');
      return;
    }

    setValidationError('');
    updateFootnotes(localFootnotes);
    updatePageDeckline(
      selectedDeckline.metadata.internalId,
      'footnotes',
      localDecklineFootnotes.length > 0 ? localDecklineFootnotes : [],
    );

    setDisplayFootnotesModal(false);
  }, [
    selectedDeckline,
    localDecklineFootnotes,
    localFootnotes,
    updatePageDeckline,
    updateFootnotes,
    setDisplayFootnotesModal,
  ]);

  /**
   * Cancel and revert all non-delete operations
   * Restores footnotes and deckline state to original values from context
   * Delete operations are not reverted as they were already committed
   */
  const handleCancel = useCallback(() => {
    updateFootnotes(contextFootnotes);
    if (selectedDeckline) {
      updatePageDeckline(selectedDeckline.metadata.internalId, 'footnotes', selectedDeckline.footnotes || null);
    }
    setDisplayFootnotesModal(false);
  }, [contextFootnotes, selectedDeckline, updateFootnotes, updatePageDeckline, setDisplayFootnotesModal]);

  if (!selectedDeckline) return null;

  return (
    <>
      <Dialog open={displayFootnotesModal} onClose={handleCancel} maxWidth='lg' fullWidth className='footnotes-modal'>
        <DialogTitle className='footnotes-modal__header'>
          <Typography variant='h6' component='div'>
            Footnotes for: {selectedDeckline.metadata.actual_kpi_name}
          </Typography>
        </DialogTitle>

        <DialogContent className='footnotes-modal__content'>
          <Typography variant='body2'>
            Add new footnotes or link/unlink existing footnotes in the table. Click a footnote to edit, this is global
            and will change all linked instances of this footnote.
          </Typography>
          <FootnotesTable
            data={tableData}
            onUpdateRow={handleUpdateRow}
            onDeleteRow={handleDeleteRow}
            isEditingDisabled={isEditingDisabled}
          />
          {validationError && (
            <Alert severity='error' onClose={() => setValidationError('')} className='footnotes-modal__alert'>
              {validationError}
            </Alert>
          )}
        </DialogContent>

        <DialogActions className='footnotes-modal__footer'>
          <Button
            variant='outlined'
            startIcon={<AddIcon />}
            onClick={handleAddFootnote}
            className='footnotes-modal__add-button'
            disabled={isEditingDisabled}
          >
            Add New Footnote
          </Button>
          <Box className='footnotes-modal__spacer' />
          <Button onClick={handleCancel} variant='outlined'>
            Cancel
          </Button>
          <Button onClick={handleSave} variant='contained' color='primary' disabled={isEditingDisabled}>
            Save
          </Button>
        </DialogActions>
      </Dialog>
      <ConfirmDialog
        dialogTitle='Delete Footnote'
        dialogText='Deleting this footnote will permanently remove it from all links on this page. This cannot be reverted.'
        isDialogOpen={deleteConfirmation.open}
        handleCancel={() => setDeleteConfirmation({ open: false, footnoteId: '' })}
        confirmText='Delete Footnote'
        handleConfirm={confirmDelete}
      />
    </>
  );
};

export default FootnotesModal;
